/**
 * Lazy-loader for OneSignal.
 *
 * Replaces the previous pattern of `import OneSignal from "react-onesignal"`
 * + `OneSignal.init(...)` inside `AppProviders`'s `useEffect`. That eager
 * approach had two drawbacks:
 *
 *   1. The ~70 KB SDK landed in the shared `AppProviders` chunk that every
 *      `(app)`, `(admin)`, AND `(embed)` route loads. The embed iframe
 *      never opts users in to push notifications, so the bytes were wasted.
 *   2. `OneSignal.init(...)` fired a network call on every authenticated
 *      route, including embeds and short-lived pages like `/success`.
 *
 * `getOneSignal()` defers both: the dynamic `import()` only fetches the
 * SDK chunk on first call, and `init` is run exactly once (subsequent
 * callers wait on the same promise). Server-side calls reject early so
 * accidental SSR access is loud, not silent.
 *
 * Usage:
 *   const OS = await getOneSignal();
 *   OS.login(profile.id);
 *
 * Callers that read OneSignal state synchronously today (e.g.
 * `OneSignal.User.PushSubscription.optedIn` to seed initial UI state) need
 * to be rewritten to await the helper.
 */
let onesignalPromise: Promise<typeof import("react-onesignal").default> | null =
  null;

export function getOneSignal() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("OneSignal: not available on the server"));
  }
  if (!onesignalPromise) {
    onesignalPromise = import("react-onesignal").then(async (mod) => {
      const OneSignal = mod.default;
      await OneSignal.init({
        appId: process.env.NEXT_PUBLIC_ONE_SIGNAL_APP_ID as string,
        autoResubscribe: true,
      });
      return OneSignal;
    });
  }
  return onesignalPromise;
}
